/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import React from 'react';
import { Row, Col } from 'antd';

import { useTag } from '@api/hooks';
import { Radio, Typography } from '@atoms';
import { ArticleTagField, ColorField, ContentModuleModal, TextField } from '@molecules';
import MediaField from '@organisms/MediaField';
import { NO_MARGIN_BOTTOM } from '@constants';
import useEventColors from '@hooks/useEventColors';

import Form from '../Form';

import { FORM_FIELD, COLOR_KEY, IMAGE_KEY } from './constants';
import Item from './Item';
import s from './styles.module.less';

const KEYS = [COLOR_KEY, IMAGE_KEY];

const LatestNewsPanel = ({ visible, hide, previewImg, onDelete, onSubmit, initialValues: data, errors }) => {
  const [form] = Form.useForm(FORM_FIELD);
  const initialValues = React.useMemo(() => form.deserialize(data), [form, data]);
  const [source, setSource] = React.useState(initialValues.colour ? COLOR_KEY : IMAGE_KEY);
  const tagQuery = useTag(data?.tagId);
  const eventColors = useEventColors();

  React.useEffect(() => {
    if (visible && tagQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.TAG.name]: tagQuery.data });
    }
  }, [form, visible, tagQuery.status, tagQuery.data]);

  React.useEffect(
    () =>
      form.setFields(
        KEYS.map((name) => ({
          name: FORM_FIELD[name].name,
          errors: undefined,
        })),
      ),
    [source, form],
  );

  const handleClose = React.useCallback(() => {
    form.resetFields();
    hide();
  }, [form, hide]);

  const handleChangeSource = ({ target: { value } }) => {
    setSource(value);
  };

  const handleSubmit = React.useCallback(
    (values) => {
      onSubmit(values);
      handleClose();
    },
    [onSubmit, handleClose],
  );

  return (
    <ContentModuleModal
      visible={visible}
      title="Latest news panel"
      subtitle="The 3 most recent articles show based on the tag you choose."
      okText={onDelete ? 'SAVE CHANGES' : 'ADD PANEL'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form form={form} onSerializedFinish={handleSubmit} layout="vertical" initialValues={initialValues}>
        <TextField {...FORM_FIELD.TITLE} />
        <ArticleTagField {...FORM_FIELD.TAG} />
        <Typography.Title level={4}>Background</Typography.Title>
        <Radio.Group onChange={handleChangeSource} className={s.radioGroup} value={source}>
          <Row align="top" gutter={10}>
            <Col flex="0" className={s.radio}>
              <Radio value={IMAGE_KEY} />
            </Col>
            <Col flex="auto">
              <MediaField
                {...FORM_FIELD[IMAGE_KEY]}
                name={source === IMAGE_KEY && FORM_FIELD[IMAGE_KEY].name}
                disabled={source !== IMAGE_KEY}
                rules={source !== IMAGE_KEY ? undefined : FORM_FIELD[IMAGE_KEY].rules}
              />
            </Col>
          </Row>
          <Row align="top" gutter={10}>
            <Col flex="0" className={s.radio}>
              <Radio value={COLOR_KEY} />
            </Col>
            <Col flex="auto">
              <ColorField
                {...FORM_FIELD[COLOR_KEY]}
                name={source === COLOR_KEY && FORM_FIELD[COLOR_KEY].name}
                style={NO_MARGIN_BOTTOM}
                disabled={source !== COLOR_KEY}
                rules={source !== COLOR_KEY ? undefined : FORM_FIELD[COLOR_KEY].rules}
                mergeColors={eventColors}
              />
            </Col>
          </Row>
          <Row align="top" gutter={10}>
            <TextField {...FORM_FIELD.MORE_NEWS_LINK} style={{ marginTop: 10, width: '100%' }} />
          </Row>
        </Radio.Group>
      </Form>
    </ContentModuleModal>
  );
};

LatestNewsPanel.Item = Item;

export default LatestNewsPanel;
